import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Loader2, Calendar, Clock, MapPin, Video, ExternalLink, Bell, Check } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Workshops() {
  const { user } = useAuth();
  const [workshops, setWorkshops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpcoming, setShowUpcoming] = useState(true);
  const [notifySubscribed, setNotifySubscribed] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [emptyContent, setEmptyContent] = useState({
    headline: "Coming Soon",
    message: "The Society is currently curating the upcoming series of workshops. Members receive 48-hour priority access to registration before the general public.",
    button_text: "Notify Me When Registration Opens"
  });

  useEffect(() => {
    fetchWorkshops();
    fetchEmptyContent();
    checkNotifyStatus();
  }, [showUpcoming]);

  const checkNotifyStatus = async () => {
    try {
      const response = await axios.get(`${API}/workshops/notify-me/status`, { withCredentials: true });
      setNotifySubscribed(response.data.subscribed);
    } catch (error) {
      // Not logged in or error
    }
  };

  const handleNotifyMe = async () => {
    if (!user) {
      toast.error("Please sign in to get notified");
      return;
    }
    
    setSubscribing(true);
    try {
      const response = await axios.post(`${API}/workshops/notify-me`, {}, { withCredentials: true });
      if (response.data.already_subscribed) {
        toast.info("You're already on the notification list!");
      } else {
        toast.success("You'll be notified when workshop registration opens!");
      }
      setNotifySubscribed(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to subscribe");
    } finally {
      setSubscribing(false);
    }
  };

  const fetchEmptyContent = async () => {
    try {
      const response = await axios.get(`${API}/site-content/workshops/empty_state`);
      if (response.data.content && Object.keys(response.data.content).length > 0) {
        setEmptyContent(prev => ({ ...prev, ...response.data.content }));
      }
    } catch (error) {
      // Use defaults
    }
  };

  const fetchWorkshops = async () => {
    try {
      const url = showUpcoming 
        ? `${API}/workshops?upcoming_only=true` 
        : `${API}/workshops`;
      const response = await axios.get(url);
      setWorkshops(response.data);
    } catch (error) {
      console.error("Error fetching workshops:", error);
    } finally {
      setLoading(false);
    }
  };

  const upcomingWorkshops = workshops.filter(w => !w.replay_url);
  const pastWorkshops = workshops.filter(w => w.replay_url);

  return (
    <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-purple-light mb-4 font-mono">
            Learn & Grow
          </p>
          <h1 className="font-syne text-4xl md:text-6xl font-bold text-white mb-6">
            AI Workshops
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl">
            Training sessions from industry experts. Join live workshops to learn 
            AI filmmaking techniques.
          </p>
        </motion.div>

        {/* Filter */}
        <div className="flex gap-4 mb-12">
          <button
            onClick={() => setShowUpcoming(true)}
            data-testid="filter-upcoming"
            className={`px-6 py-3 text-xs uppercase tracking-wider transition-all ${
              showUpcoming
                ? "bg-purple text-white"
                : "bg-void-surface text-slate-400 hover:text-white border border-white/10"
            }`}
          >
            Upcoming
          </button>
          <button
            onClick={() => setShowUpcoming(false)}
            data-testid="filter-all"
            className={`px-6 py-3 text-xs uppercase tracking-wider transition-all ${
              !showUpcoming
                ? "bg-purple text-white"
                : "bg-void-surface text-slate-400 hover:text-white border border-white/10"
            }`}
          >
            All Workshops
          </button>
        </div>

        {/* Workshops List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-purple animate-spin" />
          </div>
        ) : workshops.length === 0 ? (
          <div className="text-center py-20 max-w-xl mx-auto">
            <Video className="w-16 h-16 text-purple/30 mx-auto mb-6" />
            <h2 className="font-syne text-2xl font-bold text-white mb-4">{emptyContent.headline}</h2>
            <p className="text-slate-400 mb-8">
              {emptyContent.message}
            </p>
            {notifySubscribed ? (
              <div className="inline-flex items-center gap-2 px-8 py-4 bg-green-500/20 text-green-400 font-bold uppercase tracking-widest text-xs border border-green-500/30">
                <Check className="w-4 h-4" />
                You'll Be Notified
              </div>
            ) : user ? (
              <button
                onClick={handleNotifyMe}
                disabled={subscribing}
                data-testid="workshop-notify-btn"
                className="inline-flex items-center gap-2 px-8 py-4 bg-purple text-white font-bold uppercase tracking-widest text-xs hover:bg-purple-hover transition-all disabled:opacity-50"
              >
                {subscribing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Bell className="w-4 h-4" />
                )}
                {emptyContent.button_text}
              </button>
            ) : (
              <Link
                to="/join"
                data-testid="workshop-join-btn"
                className="inline-flex items-center gap-2 px-8 py-4 bg-purple text-white font-bold uppercase tracking-widest text-xs hover:bg-purple-hover transition-all"
              >
                <Bell className="w-4 h-4" />
                Join to Get Notified
              </Link>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {workshops.map((workshop, index) => (
              <motion.div
                key={workshop.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Link
                  to={`/workshops/${workshop.id}`}
                  data-testid={`workshop-${index}`}
                  className="block p-6 md:p-8 bg-void-surface border border-white/5 hover:border-purple/30 transition-all duration-500 group"
                >
                  <div className="flex flex-col md:flex-row md:items-center gap-6">
                    <div className="flex-shrink-0">
                      <div className="w-16 h-16 bg-purple/20 flex items-center justify-center">
                        <Video className="w-8 h-8 text-purple-light" />
                      </div>
                    </div>
                    <div className="flex-grow">
                      <div className="flex flex-wrap items-center gap-4 mb-3">
                        <span className="flex items-center gap-1 text-sm text-purple-light">
                          <Calendar className="w-4 h-4" />
                          {workshop.date}
                        </span>
                        <span className="flex items-center gap-1 text-sm text-slate-400">
                          <Clock className="w-4 h-4" />
                          {workshop.time} ({workshop.duration})
                        </span>
                        <span className="flex items-center gap-1 text-sm text-slate-400">
                          <MapPin className="w-4 h-4" />
                          {workshop.is_online ? "Online" : workshop.location}
                        </span>
                        {workshop.price && (
                          <span className="px-2 py-0.5 bg-purple/20 text-purple-light text-sm font-medium">
                            {workshop.price}
                          </span>
                        )}
                      </div>
                      <h2 className="font-syne font-bold text-white text-xl mb-2 group-hover:text-purple-light transition-colors">
                        {workshop.title}
                      </h2>
                      <p className="text-slate-400 line-clamp-2 mb-2">{workshop.description}</p>
                      <p className="text-sm text-slate-500">Instructor: {workshop.instructor}</p>
                      {workshop.is_sponsored && (
                        <span className="inline-block mt-3 px-3 py-1 bg-purple/20 text-purple-light text-xs uppercase tracking-wider">
                          Sponsored by {workshop.sponsor_name}
                        </span>
                      )}
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}

        {/* Partnership CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="mt-24 p-8 md:p-12 bg-void-highlight border border-purple/20"
        >
          <h2 className="font-syne text-2xl md:text-3xl font-bold text-white mb-4">
            Partner With Us
          </h2>
          <p className="text-slate-300 mb-6 max-w-2xl">
            Interested in sponsoring a workshop or partnering with the AI Film Society? 
            We offer opportunities for brands to connect with the AI filmmaking community.
          </p>
          <a
            href="mailto:sam@aiadstudio.com?subject=AI Film Society Workshop Partnership"
            data-testid="partnership-cta"
            className="inline-flex items-center gap-2 px-6 py-3 bg-purple text-white font-bold uppercase tracking-widest text-xs rounded-full hover:bg-purple-hover transition-all"
          >
            Get In Touch
            <ExternalLink className="w-4 h-4" />
          </a>
        </motion.div>
      </div>
    </div>
  );
}
